import { useCallback, useState, useEffect, useRef } from "react";
import { useLocation, Redirect } from "react-router-dom";

import { Box, Container, Grid, Paper, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import FormStepsStates from "./EmissionsSurveySteps";

import { SurveyContext } from "../survey-shape/SurveyContext";
import {
  transactionsQuestions,
  scopesList,
  buildScopeTonsFromEmissions,
  filterVendorSurveyTransactions,
  filterTransactionsForSurveyYear,
  formatTransactionsForSurvey,
  extractVehicleFuelData,
  readSurveyProgressFromVendorSurveys,
  buildVendorSurveysWithProgress,
  addUniqueFieldId,
  removeFieldId
} from "./helpers";

import { generateTempId } from "../../../helpers/otherHelpers";
import {
  getAvailableSurveyYears,
  hasEffectiveStripeCustomerId
} from "../../../helpers/components/vendors";
import { analyticsTrack } from "../../../helpers/analytics";

import * as Sentry from "@sentry/react";

import {
  useAuth,
  useAccountData,
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../../helpers/firebase";
import useExplorerLayoutContext from "../../../helpers/contexts/explorerLayout";

const AccountDataLoadErrorBlock = () => (
  <Box p={4} display="flex" justifyContent="center">
    <Container maxWidth="sm">
      <Paper>
        <Box p={4}>
          <Grid container direction="column" spacing={2} alignItems="center">
            <Grid item>
              <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            </Grid>
            <Grid item>
              <Typography variant="h6" align="center">
                We couldn&apos;t load your account
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textSecondary" align="center">
                Something went wrong on our end. Please refresh the page or
                contact support@aclymate.com if the issue continues.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  </Box>
);

const ExplorerEmissionsSurvey = () => {
  const location = useLocation();
  const yearParam = new URLSearchParams(location.search).get("year");

  const [user, userLoading] = useAuth();
  const { uid: userId } = user || {};
  const vendorId = window.sessionStorage.getItem("accountId");

  const [accountData, accountDataLoading, accountDataError] = useAccountData();
  const [offices, officesLoading] = useCachedDisplayData("offices");
  const [allTransactions, transactionsLoading] =
    useCachedDisplayData("transactions");
  const [emissions, emissionsLoading] = useCachedDisplayData("emissions");
  const { updateAccountData, updateCollectionDoc, newCollectionDoc } =
    useCachedFirebaseCrud();

  const dbDataLoading =
    userLoading ||
    accountDataLoading ||
    officesLoading ||
    transactionsLoading ||
    emissionsLoading;

  const [formStep, setFormStep] = useState(0);
  const [selectedYear, setSelectedYear] = useState(
    yearParam ? Number(yearParam) : null
  );
  const [vendorData, setVendorData] = useState({});
  const [facilities, setFacilities] = useState([{}]);
  const [transactions, setTransactions] = useState(
    transactionsQuestions.map(() => ({}))
  );
  const [vehicleFuelData, setVehicleFuelData] = useState({});
  const [scopeTons, setScopeTons] = useState(
    scopesList.map(({ name }) => ({ scope: name, tons: "", unknown: false }))
  );
  const [formStepsCount, setFormStepsCount] = useState(0);
  const hydratedVendorDataRef = useRef(false);
  const hydratedYearRef = useRef(null);

  useEffect(() => {
    if (dbDataLoading || hydratedVendorDataRef.current || !accountData) {
      return;
    }
    hydratedVendorDataRef.current = true;
    setVendorData(accountData);
  }, [dbDataLoading, accountData]);

  const isDefaultFacilitiesState = (facilitiesArray) => {
    if (!Array.isArray(facilitiesArray) || facilitiesArray.length === 0) {
      return true;
    }
    if (facilitiesArray.length > 1) {
      return false;
    }
    return Object.keys(facilitiesArray[0] || {}).length === 0;
  };

  useEffect(() => {
    if (dbDataLoading || !offices || !offices.length) {
      return;
    }
    if (!isDefaultFacilitiesState(facilities)) {
      return;
    }
    setFacilities(offices);
  }, [dbDataLoading, offices, facilities]);

  useEffect(() => {
    if (dbDataLoading || selectedYear) {
      return;
    }
    const candidates = getAvailableSurveyYears(
      accountData?.vendorSurveys || []
    );
    if (candidates.length === 1) {
      setSelectedYear(candidates[0]);
    }
  }, [dbDataLoading, selectedYear, accountData]);

  useEffect(() => {
    if (!selectedYear || dbDataLoading) {
      return;
    }
    if (hydratedYearRef.current === selectedYear) {
      return;
    }
    hydratedYearRef.current = selectedYear;

    const surveyTransactionsForYear = filterTransactionsForSurveyYear(
      filterVendorSurveyTransactions(allTransactions),
      selectedYear
    );

    const nextTransactions = surveyTransactionsForYear.length
      ? formatTransactionsForSurvey(surveyTransactionsForYear)
      : transactionsQuestions.map(() => ({}));
    const nextVehicleFuel = surveyTransactionsForYear.length
      ? extractVehicleFuelData(surveyTransactionsForYear)
      : {};
    setTransactions(nextTransactions);
    setVehicleFuelData(nextVehicleFuel);

    const builtScopeTons = buildScopeTonsFromEmissions(emissions, selectedYear);
    const nextScopeTons = builtScopeTons.length
      ? builtScopeTons
      : scopesList.map(({ name }) => ({
          scope: name,
          tons: "",
          unknown: false
        }));
    setScopeTons(nextScopeTons);
  }, [selectedYear, dbDataLoading, allTransactions, emissions]);

  const [editingFromStep, setEditingFromStep] = useState(null);
  const editingFromStepRef = useRef(null);
  useEffect(() => {
    editingFromStepRef.current = editingFromStep;
  }, [editingFromStep]);

  const advanceOrReturn = useCallback(() => {
    if (editingFromStepRef.current !== null) {
      const target = editingFromStepRef.current;
      editingFromStepRef.current = null;
      setEditingFromStep(null);
      setFormStep(target);
      return;
    }
    setFormStep((currentStep) => currentStep + 1);
  }, []);

  const onNextStep = useCallback(() => advanceOrReturn(), [advanceOrReturn]);
  const onPrevStep = () => setFormStep((currentStep) => currentStep - 1);

  const startEditingField = useCallback((targetStepIndex) => {
    setFormStep((currentStep) => {
      if (editingFromStepRef.current === null) {
        editingFromStepRef.current = currentStep;
        setEditingFromStep(currentStep);
      }
      return targetStepIndex;
    });
  }, []);

  const { activateSnackbar } = useExplorerLayoutContext() || {};

  const submitCurrentStepRef = useRef(null);
  const registerStepSubmit = useCallback((submitFn) => {
    submitCurrentStepRef.current = submitFn;
  }, []);
  const isAdvancingRef = useRef(false);
  const pendingAdvanceRef = useRef(false);
  const advanceCurrentStep = useCallback(async () => {
    if (isAdvancingRef.current) {
      pendingAdvanceRef.current = true;
      return;
    }
    const saveFn = submitCurrentStepRef.current;
    if (!saveFn) {
      advanceOrReturn();
      return;
    }
    isAdvancingRef.current = true;
    try {
      await saveFn();
    } catch (err) {
      isAdvancingRef.current = false;
      Sentry.captureException(err, {
        tags: { feature: "explorer-survey-advance" },
        extra: { vendorId, selectedYear }
      });
      if (activateSnackbar) {
        activateSnackbar({
          severity: "error",
          message:
            "Couldn't save that answer — please try again. Your previous answers are still saved."
        });
      }
      return;
    }
    isAdvancingRef.current = false;
    if (pendingAdvanceRef.current) {
      pendingAdvanceRef.current = false;
      advanceCurrentStep();
      return;
    }
    advanceOrReturn();
  }, [advanceOrReturn, activateSnackbar, vendorId, selectedYear]);

  const [isScoutCollapsed, setIsScoutCollapsed] = useState(false);
  const [surveyCompletionStatus, setSurveyCompletionStatus] = useState("idle");

  const [touchedFields, setTouchedFields] = useState(() => new Set());
  const touchField = useCallback((fieldId) => {
    if (!fieldId) {
      return;
    }
    setTouchedFields((prev) => {
      if (prev.has(fieldId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(fieldId);
      return next;
    });
  }, []);

  const availableYears = getAvailableSurveyYears(
    accountData?.vendorSurveys || []
  );

  const {
    skippedFields,
    unknownFields,
    conversationId: persistedConvId
  } = readSurveyProgressFromVendorSurveys(
    accountData?.vendorSurveys,
    selectedYear
  );

  const vendorSurveysRef = useRef(accountData?.vendorSurveys);
  useEffect(() => {
    vendorSurveysRef.current = accountData?.vendorSurveys;
  }, [accountData?.vendorSurveys]);

  const writeSurveyProgress = (patch) => {
    if (!selectedYear) {
      return Promise.resolve();
    }
    const priorVendorSurveys = Array.isArray(vendorSurveysRef.current)
      ? vendorSurveysRef.current
      : [];
    const isFirstEntryForYear = !priorVendorSurveys.some(
      ({ year }) => Number(year) === Number(selectedYear)
    );
    const updatedVendorSurveys = buildVendorSurveysWithProgress(
      priorVendorSurveys,
      selectedYear,
      patch
    );
    vendorSurveysRef.current = updatedVendorSurveys;
    const updatePromise = updateAccountData({
      vendorSurveys: updatedVendorSurveys
    });
    if (isFirstEntryForYear) {
      analyticsTrack("survey_started", {
        surveyType: "emissions",
        year: Number(selectedYear),
        timestamp: Date.now()
      });
    }
    return updatePromise;
  };

  const ensureConversationId = async () => {
    if (persistedConvId) {
      const coerced = String(persistedConvId);
      if (typeof persistedConvId !== "string") {
        await writeSurveyProgress({ conversationId: coerced });
      }
      return coerced;
    }
    const newId = String(generateTempId());
    await writeSurveyProgress({ conversationId: newId });
    return newId;
  };

  const skipQuestion = (fieldId) =>
    writeSurveyProgress({
      skippedFields: addUniqueFieldId(skippedFields, fieldId),
      unknownFields: removeFieldId(unknownFields, fieldId)
    });

  const markUnknown = (fieldId) =>
    writeSurveyProgress({
      unknownFields: addUniqueFieldId(unknownFields, fieldId),
      skippedFields: removeFieldId(skippedFields, fieldId)
    });

  const clearSkipUnknownMarkers = (fieldId) => {
    const wasSkipped = skippedFields.includes(fieldId);
    const wasUnknown = unknownFields.includes(fieldId);
    if (!wasSkipped && !wasUnknown) {
      return Promise.resolve();
    }
    return writeSurveyProgress({
      skippedFields: removeFieldId(skippedFields, fieldId),
      unknownFields: removeFieldId(unknownFields, fieldId)
    });
  };

  // A Navigator customer (real Stripe subscription, real accounting-synced
  // transactions/closed years) has no business in this survey regardless of
  // how it navigated here -- onSurveyComplete's backend endpoint deletes and
  // recreates this year's transactions/aggregation doc unconditionally, with
  // no closed-year check of its own. useShareCustomerYear already avoids
  // sending a Navigator customer into this route via its one known caller;
  // this is the same check applied at the entry point itself, so it holds
  // regardless of how the route was reached (a bookmark, a direct link, an
  // admin/tester view, or any future caller).
  //
  if (!accountDataLoading && hasEffectiveStripeCustomerId(accountData)) {
    return <Redirect to="/explorer/dashboard" />;
  }

  // Fail closed on accountDataError, but don't silently redirect: a rejected
  // fetch leaves accountData at its empty default (no stripeCustomerId), so
  // a transient read failure must not be read as "confirmed non-Navigator" --
  // that would let the destructive path through on exactly the accounts a
  // real error is more likely to hit (production accounts under load). A
  // redirect here previously bounced the user straight to the dashboard with
  // no explanation, which looked identical to "retaking the survey isn't
  // possible" -- showing the error in place lets a transient glitch surface
  // as a transient glitch instead of a dead end.
  if (accountDataError) {
    return <AccountDataLoadErrorBlock />;
  }

  return (
    <SurveyContext.Provider
      value={{
        userId,
        vendorId,
        selectedYear,
        setSelectedYear,
        vendorData,
        setVendorData,
        facilities,
        setFacilities,
        transactions,
        setTransactions,
        vehicleFuelData,
        setVehicleFuelData,
        scopeTons,
        setScopeTons,
        dbDataLoading,
        formStep,
        setFormStep,
        onNextStep,
        onPrevStep,
        formStepsCount,
        setFormStepsCount,
        updateAccountData,
        updateCollectionDoc,
        newCollectionDoc,
        skippedFields,
        unknownFields,
        scoutConversationId: persistedConvId,
        ensureConversationId,
        skipQuestion,
        markUnknown,
        clearSkipUnknownMarkers,
        registerStepSubmit,
        advanceCurrentStep,
        isScoutCollapsed,
        setIsScoutCollapsed,
        surveyCompletionStatus,
        setSurveyCompletionStatus,
        availableYears,
        editingFromStep,
        startEditingField,
        touchedFields,
        touchField
      }}
    >
      <FormStepsStates />
    </SurveyContext.Provider>
  );
};
export default ExplorerEmissionsSurvey;
